import axios from 'axios';
import config from '../config';
import { refreshAccessToken, clearAuthData } from './auth';

const api = axios.create({
  baseURL: config.apiBaseUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to add the auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Credential failures on login/register/password routes must surface as
    // auth errors. Refresh recovery would mask a 401 as a network failure.
    const requestUrl = String(originalRequest?.url || '');
    const isCredentialAuthRequest = /\/api\/auth\/(login|register|forgot-password|reset-password|verify)/.test(
      requestUrl
    );

    // Admin analytics (/api/admin/*) and security (/api/security/*) now use the
    // main app JWT, so they participate in the standard refresh-on-401 flow.
    // If the error is 401 and we haven't tried to refresh the token yet
    if (error.response?.status === 401 && !originalRequest._retry && !isCredentialAuthRequest) {
      originalRequest._retry = true;

      try {
        const newAccessToken = await refreshAccessToken();
        if (!newAccessToken) {
          throw new Error('Failed to refresh token');
        }

        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // If refresh token is invalid, clear all auth data and redirect to login
        clearAuthData();
        
        // Only redirect if we're not already on the login page
        if (!window.location.pathname.includes('/login')) {
          const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/login?returnUrl=${returnUrl}`;
        }
        
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api; 